import React, { useState, useEffect, useRef } from 'react';
import './adminProfile.css';

const API_BASE_URL = 'https://car.amar.co.jp/api/authorization/user';
const ADMIN_USER_API_BASE = 'https://car.amar.co.jp/api/authorization/admin-user';
const AUTH_API_BASE = 'https://car.amar.co.jp/api/authorization/auth';

const getAuthToken = () => localStorage.getItem('adminAuthToken');

// ─── Country Code Dropdown ────────────────────────────────────────────────────
// countries: [{ countryId, countryName, countryPhoneCode, isoCode }] — from API
// onChange(country | null) - poora country object deta hai (US/Canada dono +1, isliye id se track)
const CountryCodeDropdown = ({ selectedDial, selectedId, onChange, countries = [] }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  // Pehle countryId se dhundo, na mile to dial code se (purane data ke liye)
  const selected =
    (selectedId && countries.find(c => c.countryId === selectedId)) ||
    countries.find(c => c.countryPhoneCode === selectedDial);
  // Search priority: exact ISO (IN -> India) > name startsWith > baaki matches
  // (null-safe: kuch countries ka isoCode null aata hai, e.g. Belize)
  const term = search.trim().toLowerCase();
  const filtered = !term ? countries : countries
    .filter(c =>
      (c.countryName || '').toLowerCase().includes(term) ||
      (c.countryPhoneCode || '').includes(term) ||
      (c.isoCode || '').toLowerCase().includes(term)
    )
    .map(c => {
      const name = (c.countryName || '').toLowerCase();
      const iso = (c.isoCode || '').toLowerCase();
      const rank = iso === term ? 0 : name.startsWith(term) ? 1 : 2;
      return { c, rank };
    })
    .sort((a, b) => a.rank - b.rank)
    .map(x => x.c);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <div className="ap-country-dropdown" ref={dropdownRef}>
      <div className="ap-country-trigger" onClick={() => setOpen(!open)}>
        <span className="ap-country-code">
          {selected ? `${selected.isoCode} ${selected.countryPhoneCode}` : (selectedDial || '+Code')}
        </span>
        {selectedDial && (
          <span className="ap-country-clear" onClick={(e) => { e.stopPropagation(); onChange(null); }}>×</span>
        )}
        <svg width="10" height="6" viewBox="0 0 10 6" className={`ap-country-arrow ${open ? 'open' : ''}`}>
          <path d="M1 1l4 4 4-4" stroke="#666" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      </div>
      {open && (
        <div className="ap-country-menu">
          <div className="ap-country-search-wrap">
            <input
              className="ap-country-search"
              placeholder="Search country..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="ap-country-list">
            {filtered.map(c => (
              <div
                key={c.countryId}
                className={`ap-country-option ${selected?.countryId === c.countryId ? 'active' : ''}`}
                onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
              >
                <span className="ap-option-code">{c.isoCode}</span>
                <span className="ap-option-name">{c.countryName}</span>
                <span className="ap-option-dial">{c.countryPhoneCode}</span>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="ap-country-empty">
                {countries.length === 0 ? 'Loading...' : 'No results'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Toast Notification ───────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`ap-toast ap-toast-${type}`}>
      <span className="ap-toast-icon">{type === 'success' ? '✓' : '✕'}</span>
      <span>{message}</span>
      <button className="ap-toast-close" onClick={onClose}>×</button>
    </div>
  );
};

// ─── My Profile Tab ───────────────────────────────────────────────────────────
const MyProfile = () => {
  const [profileImage, setProfileImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [countries, setCountries] = useState([]);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryDial: '',
    countryId: null,
    mobile: '',
    dob: '',
    gender: 'Male',
  });

  const showToast = (message, type = 'success') => setToast({ message, type });

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = getAuthToken();
        const res = await fetch('https://car.amar.co.jp/api/authorization/shipping/all-countries', {
          headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCountries(data);
        }
      } catch (err) {
        console.error('Fetch countries error:', err);
      }
    };
    fetchCountries();
  }, []);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/get/user/details`, {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${token}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          const d = data.userDetails || {};
          setForm({
            firstName:   d.firstName   || '',
            lastName:    d.lastName    || '',
            email:       d.email       || '',
            countryDial: d.countryCode || '+91',
            // API countryId de to exact country (US vs Canada) select hogi
            countryId:   d.countryId || null,
            mobile:      d.userMobileNumber || '',
            dob:         d.dateOfBirth || '',
            gender:      d.gender      || 'Male',
          });
        } else {
          // Fallback to localStorage
          const name = localStorage.getItem('adminName') || '';
          const parts = name.split(' ');
          setForm(prev => ({
            ...prev,
            firstName: parts[0] || '',
            lastName:  parts.slice(1).join(' ') || '',
            email:     localStorage.getItem('adminEmail') || '',
          }));
        }
      } catch (err) {
        console.error('Fetch profile error:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchImage = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/get-image/`, {
          headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const blob = await res.blob();
          setProfileImage(URL.createObjectURL(blob));
        }
      } catch (err) {
        console.error('Fetch image error:', err);
      }
    };

    fetchProfile();
    fetchImage();
  }, []);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('Image size should be less than 5MB', 'error'); return; }

    setImageUploading(true);
    try {
      const token = getAuthToken();
      // Same as MyProfile: file ko multipart form-data me bhejo (backend JSON/base64 nahi leta)
      const formData = new FormData();
      formData.append('profile-image', file);

      const res = await fetch(`${API_BASE_URL}/upload/profile-photo`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`
          // Content-Type mat lagao - browser FormData ke saath boundary khud lagata hai
        },
        body: formData
      });
      if (res.ok) {
        setProfileImage(URL.createObjectURL(file));
        showToast('Profile photo updated successfully!');
      } else {
        showToast('Failed to upload profile photo', 'error');
      }
      setImageUploading(false);
    } catch (err) {
      console.error('Upload error:', err);
      showToast('Error uploading photo', 'error');
      setImageUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.firstName.trim()) { showToast('First name is required', 'error'); return; }
    if (!form.lastName.trim()) { showToast('Last name is required', 'error'); return; }
    if (!form.email.trim()) { showToast('Email is required', 'error'); return; }

    setSaving(true);
    try {
      const token = getAuthToken();
      // Exact selected country (id se), fallback dial code
      const matchedCountry =
        (form.countryId && countries.find(c => c.countryId === form.countryId)) ||
        countries.find(c => c.countryPhoneCode === form.countryDial);
      const body = {
        firstName:         form.firstName.trim(),
        lastName:          form.lastName.trim(),
        gender:            form.gender,
        userEmailId:       form.email.trim(),
        userMobileNumber:  form.mobile.trim(),
        countryId:         matchedCountry?.countryId || null,
      };
      const adminUserId = localStorage.getItem('adminUserId');
      const res = await fetch(`${ADMIN_USER_API_BASE}/update-admin-user/${adminUserId}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        // Update localStorage name
        localStorage.setItem('adminName', `${form.firstName} ${form.lastName}`);
        localStorage.setItem('adminEmail', form.email);
        showToast('Profile saved successfully!');
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.message || 'Failed to save profile', 'error');
      }
    } catch (err) {
      console.error('Save profile error:', err);
      showToast('Error saving profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    const f = form.firstName?.charAt(0) || '';
    const l = form.lastName?.charAt(0) || '';
    return (f + l).toUpperCase() || 'A';
  };

  if (loading) return <div className="mp-loading"><div className="ap-spinner"></div></div>;

  return (
    <div className="mp-container">
      <div className="mp-card">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Red banner + floating avatar */}
      <div className="mp-banner">
        <div className="mp-avatar-wrap">
          <div className="mp-avatar-ring">
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="mp-avatar-img" />
            ) : (
              <div className="mp-avatar-initials">{getInitials()}</div>
            )}
            {imageUploading && (
              <div className="mp-avatar-overlay">
                <div className="ap-spinner ap-spinner-sm"></div>
              </div>
            )}
            <button
              className="mp-avatar-edit-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageUploading}
              title="Change photo"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="white">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </div>

      {/* Form body */}
      <div className="mp-body">
        {/* Name + role display */}
        <div className="mp-name-row">
          <p className="mp-display-name">
            {form.firstName || form.lastName ? `${form.firstName} ${form.lastName}`.trim() : 'Admin User'}
          </p>
          <p className="mp-display-role">{localStorage.getItem('adminRole') || 'Administrator'}</p>
        </div>

        <p className="mp-section-label">Personal Information</p>

        <div className="mp-grid">
          {/* First Name */}
          <div className="mp-field">
            <label className="mp-label">First Name <span className="mp-req">*</span></label>
            <input
              className="mp-input"
              type="text"
              value={form.firstName}
              onChange={e => setForm({ ...form, firstName: e.target.value })}
              placeholder="First Name"
            />
          </div>

          {/* Last Name */}
          <div className="mp-field">
            <label className="mp-label">Last Name <span className="mp-req">*</span></label>
            <input
              className="mp-input"
              type="text"
              value={form.lastName}
              onChange={e => setForm({ ...form, lastName: e.target.value })}
              placeholder="Last Name"
            />
          </div>

          {/* Email — full width */}
          <div className="mp-field mp-field-full">
            <label className="mp-label">Email <span className="mp-req">*</span></label>
            <input
              className="mp-input"
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="Email Address"
            />
          </div>

          {/* Mobile — full width */}
          <div className="mp-field mp-field-full">
            <label className="mp-label">Mobile Number <span className="mp-req">*</span></label>
            <div className="mp-mobile-row">
              <CountryCodeDropdown
                selectedDial={form.countryDial}
                selectedId={form.countryId}
                // Country badla to mobile number reset - user naya number daalega
                onChange={country => setForm(prev => {
                  const newId = country?.countryId || null;
                  // Id na ho (API ne countryId nahi diya) to dial code se compare
                  const sameCountry = !!country && (prev.countryId
                    ? newId === prev.countryId
                    : country.countryPhoneCode === prev.countryDial);
                  return {
                    ...prev,
                    countryDial: country?.countryPhoneCode || '',
                    countryId: newId,
                    mobile: sameCountry ? prev.mobile : ''
                  };
                })}
                countries={countries}
              />
              <input
                className="mp-input mp-mobile-input"
                type="tel"
                value={form.mobile}
                onChange={e => setForm({ ...form, mobile: e.target.value })}
                placeholder="Mobile Number"
              />
            </div>
          </div>

          {/* DOB */}
          <div className="mp-field">
            <label className="mp-label">Date of Birth</label>
            <input
              className="mp-input"
              type="date"
              value={form.dob}
              onChange={e => setForm({ ...form, dob: e.target.value })}
            />
          </div>

          {/* Gender */}
          <div className="mp-field">
            <label className="mp-label">Gender</label>
            <div className="mp-gender-row">
              <label className="mp-gender-option">
                <input
                  type="radio"
                  name="ap-gender"
                  value="Male"
                  checked={form.gender === 'Male'}
                  onChange={() => setForm({ ...form, gender: 'Male' })}
                />
                Male
              </label>
              <label className="mp-gender-option">
                <input
                  type="radio"
                  name="ap-gender"
                  value="Female"
                  checked={form.gender === 'Female'}
                  onChange={() => setForm({ ...form, gender: 'Female' })}
                />
                Female
              </label>
            </div>
          </div>

          {/* Save button — full width */}
          <div className="mp-field mp-field-full">
            <button className="mp-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'SAVE PROFILE'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

// ─── Change Password Tab ──────────────────────────────────────────────────────
const ChangePassword = () => {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ current: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const getStrength = (pwd) => {
    if (!pwd) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 1) return { level: 1, label: 'Weak', color: '#e53e3e' };
    if (score === 2) return { level: 2, label: 'Fair', color: '#e57c00' };
    if (score === 3) return { level: 3, label: 'Good', color: '#2f855a' };
    return { level: 4, label: 'Strong', color: '#276749' };
  };

  const strength = getStrength(form.newPassword);

  const handleChange = async () => {
    if (!form.currentPassword) { showToast('Current password is required', 'error'); return; }
    if (!form.newPassword) { showToast('New password is required', 'error'); return; }
    if (form.newPassword.length < 8) { showToast('Password must be at least 8 characters', 'error'); return; }
    if (form.newPassword !== form.confirmPassword) { showToast('Passwords do not match', 'error'); return; }

    setSaving(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${AUTH_API_BASE}/change-password`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
          confirmPassword: form.confirmPassword,
        })
      });

      if (res.ok) {
        showToast('Password changed successfully!');
        setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.message || 'Failed to change password', 'error');
      }
    } catch (err) {
      console.error('Change password error:', err);
      showToast('Error changing password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const EyeIcon = ({ visible }) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="#999">
      {visible ? (
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
      ) : (
        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
      )}
    </svg>
  );

  return (
    <div className="cp-container">
      <div className="cp-card">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Red banner header */}
      <div className="cp-banner">
        <div className="cp-banner-icon">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="white">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
          </svg>
        </div>
        <div className="cp-banner-text">
          <h2>Change Password</h2>
          <p>Update your password to keep your account secure</p>
        </div>
      </div>

      {/* Form body */}
      <div className="cp-body">
        <div className="cp-grid">

          {/* Current Password — full width */}
          <div className="cp-field cp-field-full">
            <label className="cp-label">Current Password <span className="cp-req">*</span></label>
            <div className="cp-input-wrap">
              <input
                className="cp-input"
                type={show.current ? 'text' : 'password'}
                value={form.currentPassword}
                onChange={e => setForm({ ...form, currentPassword: e.target.value })}
                placeholder="Enter current password"
              />
              <button className="cp-eye-btn" onClick={() => setShow({ ...show, current: !show.current })}>
                <EyeIcon visible={show.current} />
              </button>
            </div>
          </div>

          {/* New Password */}
          <div className="cp-field">
            <label className="cp-label">New Password <span className="cp-req">*</span></label>
            <div className="cp-input-wrap">
              <input
                className="cp-input"
                type={show.new ? 'text' : 'password'}
                value={form.newPassword}
                onChange={e => setForm({ ...form, newPassword: e.target.value })}
                placeholder="Enter new password"
              />
              <button className="cp-eye-btn" onClick={() => setShow({ ...show, new: !show.new })}>
                <EyeIcon visible={show.new} />
              </button>
            </div>
            {form.newPassword && (
              <div className="cp-strength-wrap">
                <div className="cp-strength-bars">
                  {[1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className="cp-strength-bar"
                      style={{ background: i <= strength.level ? strength.color : '#e2e8f0' }}
                    />
                  ))}
                </div>
                <span className="cp-strength-label" style={{ color: strength.color }}>{strength.label}</span>
              </div>
            )}
            <p className="cp-hint-text">Min 8 chars, uppercase, numbers, symbols</p>
          </div>

          {/* Confirm Password */}
          <div className="cp-field">
            <label className="cp-label">Confirm New Password <span className="cp-req">*</span></label>
            <div className="cp-input-wrap">
              <input
                className={`cp-input ${form.confirmPassword && form.confirmPassword !== form.newPassword ? 'cp-input-error' : ''}`}
                type={show.confirm ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
              />
              <button className="cp-eye-btn" onClick={() => setShow({ ...show, confirm: !show.confirm })}>
                <EyeIcon visible={show.confirm} />
              </button>
            </div>
            {form.confirmPassword && form.confirmPassword !== form.newPassword && (
              <p className="cp-error-text">Passwords do not match</p>
            )}
          </div>

          {/* Button — full width */}
          <div className="cp-field cp-field-full">
            <button className="cp-save-btn" onClick={handleChange} disabled={saving}>
              {saving ? 'Updating...' : 'UPDATE PASSWORD'}
            </button>
          </div>

        </div>
      </div>
      </div>
    </div>
  );
};// ─── Main AdminProfile Component ──────────────────────────────────────────────
const AdminProfile = ({ initialTab = 'profile' }) => {
  return initialTab === 'profile' ? <MyProfile /> : <ChangePassword />;
};

export default AdminProfile;